import { useEffect, useMemo } from "react";

import { useAppSelector } from "app/hooks";
import {
  PRE_AUTH_USER_ID,
  selectCurrentClinic,
  selectCurrentUser
} from "features/auth/auth-slice";
import { NewUserNotificationType } from "hooks/use-notifications";
import { useGetNotificationsQuery } from "services/notifications";
import { Notification, NotificationTypes } from "services/types/notifications";

import { isSalesReviewNotification } from "./sales-review-notification.util";

/**
 * `CustomLimitOffsetPagination.max_limit`, and it must be passed explicitly.
 *
 * Verified against the local API: `limit=300` returned 286 rows, and **omitting the
 * param returned exactly 5** — the endpoint's `default_limit`. So a dropped limit does
 * not degrade to "slightly fewer rows", it silently caps the whole correction at 5.
 *
 * The cap applies to UNREAD type-8 rows only, not to total unread. Above it we
 * under-subtract; there is no signal, because `LimitOffsetPagination.get_limit`
 * clamps rather than erroring.
 */
const UNREAD_SCAN_LIMIT = 300;

/** `lastJsonMessage` from `useNotifications`; the socket carries several shapes. */
type SocketFrame = { type?: string; message?: Notification } | null | undefined;

type Params = {
  /**
   * False when there is nothing to correct — Sales Coach is reachable, the flag has
   * not resolved yet, or nothing is unread. Off means no request at all.
   */
  enabled: boolean;
  lastJsonMessage: SocketFrame;
};

/**
 * How many of this user's unread notifications are Sales Coach reviews that the drawer
 * is hiding (SN-19629).
 *
 * The bell badge and the "All" tab both render `unreadNotificationsCount.all`, which
 * sums `GET /notifications/unread-counts/` — a server aggregate grouped by `type`.
 * Sales reviews reuse type 8 alongside the four scribe senders and are told apart only
 * by `extra.review_id`, a key inside a `JSONField` that the viewset's filterset cannot
 * reach. So the server cannot exclude them and the count has to be corrected here.
 *
 * This deliberately reuses `getNotifications` rather than adding an endpoint:
 * `NOTIFICATIONS_QUERY` already projects `type`, `is_read` and `extra`, so the rows
 * arrive as real `Notification`s and `isSalesReviewNotification` applies directly —
 * the discriminator keeps exactly one home. The cost is a ~2x payload from the
 * `title`/`description`/`clinic` fields we ignore; a narrow projection is the escape
 * hatch if that ever matters.
 */
export default function useHiddenSalesReviewCount({
  enabled,
  lastJsonMessage
}: Params): number {
  const user = useAppSelector(selectCurrentUser);
  const currentClinic = useAppSelector(selectCurrentClinic);

  // Mirrors the guard in `use-notifications.tsx`: the user object is a placeholder
  // with id -1 until auth resolves, so querying before that asks about nobody.
  const skip =
    !enabled || !currentClinic?.id || !user?.id || user.id === PRE_AUTH_USER_ID;

  const { data, refetch, isUninitialized } = useGetNotificationsQuery(
    {
      // `provider` is correctness-critical, not incidental: without it the read is
      // only practice-scoped and would count every colleague's notifications. It and
      // `clinic` must match what `getUnreadCounts` sends, or the two numbers describe
      // different sets and subtracting one from the other is meaningless.
      // `?? PRE_AUTH_USER_ID` keeps this consistent with the `!user?.id` guard above:
      // the args object is built even when the query is skipped, so reading `user.id`
      // unguarded here would crash on the one state the guard exists to catch.
      provider: user?.id ?? PRE_AUTH_USER_ID,
      clinic: currentClinic?.id,
      type: NotificationTypes.AMBIENT_SCRIBE,
      is_read: false,
      limit: UNREAD_SCAN_LIMIT
    },
    { skip }
  );

  // `use-notifications` refetches its own two queries when a notification arrives but
  // cannot refetch this one, so without a trigger a fresh sales review pushes `.all`
  // up while this count stays stale and the badge drifts upward for the whole session.
  //
  // The dependency is the FRAME, not a derived boolean: a boolean would stay `true`
  // across two consecutive sales reviews and the second would never refetch.
  //
  // Deliberately not refetching on every frame — this socket also carries
  // `call.incoming`, `stafftaskcomment.create.notification`, CSV-import completion and
  // bulk-tag events, so a busy clinic would refetch continuously.
  useEffect(() => {
    if (skip || isUninitialized) return;
    if (lastJsonMessage?.type !== NewUserNotificationType) return;
    if (
      !lastJsonMessage.message ||
      !isSalesReviewNotification(lastJsonMessage.message)
    ) {
      return;
    }
    refetch();
  }, [lastJsonMessage, skip, isUninitialized, refetch]);

  return useMemo(() => {
    // RTK's `skip` does NOT clear `data` (root CLAUDE.md), so the previous response is
    // still here after the flag flips on — or after a logout drops the user to the
    // pre-auth placeholder. Gate on `skip` rather than `enabled` so every reason the
    // query stopped also stops the correction; otherwise it outlives its own
    // justification and the badge under-counts.
    if (skip || !data) return 0;

    // `!is_read` re-check on top of the server filter. `is_read=false` IS honored
    // (verified against the API), but this account holds 286 active type-8 rows
    // against 3 unread ones — so if that param ever stopped filtering, subtracting
    // read rows would wipe the badge entirely. One `&&` buys immunity from that.
    return data.filter(
      (notification) =>
        !notification.is_read && isSalesReviewNotification(notification)
    ).length;
  }, [data, skip]);
}
